import { useState } from 'react';
import { partnerErrorMessage } from '../state/partnerError';
import { Unavailable } from '../components/Unavailable';
import { partnerGateway } from '../services';
import { useLoad } from '../state/useLoad';
import { usePartner } from '../state/usePartner';
import { PARTNER_ROLE_LABELS, type PartnerRole } from '../types';

const ROLES: PartnerRole[] = ['Owner', 'Admin', 'PayrollManager', 'PayrollProcessor', 'ReadOnly'];

/** What each role is for, in plain words. The server decides what each role may do; this is the explanation, not the rule. */
const ROLE_SUMMARY: Record<PartnerRole, string> = {
  Owner: 'Manages the firm: its profile, agreement, team, clients and revenue. A firm always keeps at least one Owner.',
  Admin: 'Runs the firm day to day: clients, assignments and team. Cannot change owners, accept agreements or see revenue.',
  PayrollManager: 'Supervises client payroll work: reviews what processors prepared and assigns client work. Cannot prepare payroll.',
  PayrollProcessor: 'Sets up client payroll information and prepares payroll. Cannot review or approve what they prepared, and cannot release money.',
  ReadOnly: 'Views assigned clients and reports. Cannot change anything.',
};

export function PartnerTeamPage() {
  const { membership, can } = usePartner();
  const orgId = membership.partnerOrganizationId;
  const team = useLoad((signal) => partnerGateway.listTeam(orgId, signal), orgId, 'Your team could not be loaded.');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const manage = can('partner.team.manage') && membership.status === 'Active';

  async function act(work: () => Promise<unknown>, failure: string) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await team.reload();
    } catch (e) {
      setError(partnerErrorMessage(e, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Team</h1>
      {error && <p role="alert" className="pp-error">{error}</p>}
      {team.error && <p role="alert" className="pp-error">{team.error}</p>}
      {team.loading && !team.data && <p className="pp-muted">Loading…</p>}

      {team.data && (
        <div className="pp-table-wrap">
          <table className="pp-table">
            <caption className="pp-sr">Partner team members</caption>
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                {manage && <th scope="col"><span className="pp-sr">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {team.data.map((m) => (
                <tr key={m.membershipId}>
                  <th scope="row">{m.displayName ?? m.email}<span className="pp-sub">{m.displayName ? m.email : ''}</span></th>
                  <td>
                    {manage && m.isActive ? (
                      <select
                        aria-label={`Role for ${m.displayName ?? m.email}`}
                        value={m.role}
                        disabled={busy}
                        onChange={(e) => void act(() => partnerGateway.changeRole(orgId, m.membershipId, e.target.value as PartnerRole), 'The role could not be changed.')}
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{PARTNER_ROLE_LABELS[r]}</option>)}
                      </select>
                    ) : (
                      PARTNER_ROLE_LABELS[m.role]
                    )}
                  </td>
                  <td>{m.isActive ? 'Active' : 'Removed'}</td>
                  {manage && (
                    <td>
                      {m.isActive && removing !== m.membershipId && <button type="button" className="pp-link-button" onClick={() => setRemoving(m.membershipId)}>Remove…</button>}
                      {removing === m.membershipId && (
                        <span role="group" aria-label={`Confirm removing ${m.displayName ?? m.email}`}>
                          They lose access on their next request.{' '}
                          <button type="button" className="pp-button pp-button--danger" disabled={busy} onClick={() => void act(async () => { await partnerGateway.deactivateMember(orgId, m.membershipId); setRemoving(null); }, 'The person could not be removed.')}>Remove</button>{' '}
                          <button type="button" className="pp-button" onClick={() => setRemoving(null)}>Cancel</button>
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section className="pp-section" aria-labelledby="pp-add-member">
        <h2 id="pp-add-member">Add a team member</h2>
        <Unavailable>Inviting people needs email delivery, which is not available yet.</Unavailable>
      </section>

      <section className="pp-section" id="roles" aria-labelledby="pp-roles">
        <h2 id="pp-roles">Roles and permissions</h2>
        <dl className="pp-roles">
          {ROLES.map((r) => (
            <div key={r}>
              <dt>{PARTNER_ROLE_LABELS[r]}</dt>
              <dd>{ROLE_SUMMARY[r]}</dd>
            </div>
          ))}
        </dl>
        <p className="pp-muted">Partner roles are separate from company roles. A partner user never becomes the owner of a client company by working for it.</p>
      </section>
    </>
  );
}
